
import { defineComponent, ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import ApiService from "@/core/services/ApiService";
import { flagUrl, countryName } from "./countries";

export default defineComponent({
  name: "MyGroup",
  setup() {
    const store = useStore();
    const loading = ref(true);
    const busy = ref(false);
    const group = ref<any>(null);
    const invitations = ref<any[]>([]);
    const inviteNick = ref("");
    const error = ref("");
    const notice = ref("");
    const iconInput = ref<HTMLInputElement | null>(null);
    const editForm = reactive({ name: "", tag: "" });

    const currentUser = computed(() => store.getters.currentUser);
    const isCaptain = computed(
      () => group.value && group.value.captain_id === currentUser.value?.id
    );

    const load = async () => {
      error.value = "";
      const { data } = await ApiService.get("groups/me");
      group.value = data.group;
      invitations.value = data.invitations ?? [];
      if (group.value) {
        editForm.name = group.value.name;
        editForm.tag = group.value.tag;
      }
    };

    const editValid = computed(
      () => editForm.name.length >= 3 && editForm.tag.length >= 2
    );
    const editChanged = computed(
      () =>
        !!group.value &&
        (editForm.name !== group.value.name ||
          editForm.tag.toUpperCase() !== group.value.tag)
    );

    const run = async (fn: () => Promise<any>, successMsg = "") => {
      busy.value = true;
      error.value = "";
      notice.value = "";
      try {
        await fn();
        await load();
        if (successMsg) notice.value = successMsg;
      } catch (e: any) {
        error.value = e?.response?.data?.message ?? "Ocorreu um erro.";
      } finally {
        busy.value = false;
      }
    };

    const saveEdit = () =>
      run(async () => {
        const payload = {
          name: editForm.name,
          tag: editForm.tag.toUpperCase(),
        };
        await ApiService.put(`groups/${group.value.id}`, payload as any);
      }, "Grupo atualizado.");

    const sendInvite = () =>
      run(async () => {
        const payload = { nick: inviteNick.value };
        await ApiService.post(
          `groups/${group.value.id}/invitations`,
          payload as any
        );
        inviteNick.value = "";
      }, "Convite enviado.");

    const cancelInvite = (invId: number) =>
      run(() => ApiService.delete(`groups/${group.value.id}/invitations/${invId}`));

    const removeMember = (memberId: number) =>
      run(() =>
        ApiService.post(`groups/${group.value.id}/members/${memberId}/remove`, {})
      );

    const transferCaptain = (member: any) => {
      const name = member.nick || member.name;
      if (
        !confirm(
          `Transferir a capitania do time para ${name}? Você passará a ser membro.`
        )
      )
        return;
      run(
        () =>
          ApiService.post(
            `groups/${group.value.id}/members/${member.id}/transfer`,
            {}
          ),
        "Capitania transferida."
      );
    };

    const uploadIcon = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      const formData = new FormData();
      formData.append("image", file);
      run(
        () => ApiService.post(`groups/${group.value.id}/icon`, formData as any),
        "Ícone atualizado."
      ).finally(() => {
        if (iconInput.value) iconInput.value.value = "";
      });
    };

    const acceptInvite = (invId: number) =>
      run(() => ApiService.post(`groups/invitations/${invId}/accept`, {}));

    const declineInvite = (invId: number) =>
      run(() => ApiService.post(`groups/invitations/${invId}/decline`, {}));

    const leaveGroup = () => {
      if (!confirm("Tem certeza que deseja sair do time?")) return;
      run(() => ApiService.post("groups/leave", {}));
    };

    const dissolveGroup = () => {
      if (!confirm("Dissolver o time? Esta ação não pode ser desfeita.")) return;
      run(() => ApiService.delete(`groups/${group.value.id}`));
    };

    onMounted(async () => {
      try {
        await load();
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      busy,
      group,
      invitations,
      inviteNick,
      error,
      notice,
      iconInput,
      editForm,
      editValid,
      editChanged,
      isCaptain,
      saveEdit,
      sendInvite,
      cancelInvite,
      removeMember,
      transferCaptain,
      uploadIcon,
      acceptInvite,
      declineInvite,
      leaveGroup,
      dissolveGroup,
      flagUrl,
      countryName,
      minMembers: 2,
      maxMembers: 10,
    };
  },
});
